import React from 'react';
import { Alert, AlertColor, Box, CssBaseline, Divider, Theme, useMediaQuery, useTheme, Grid } from '@mui/material';
import { GlobalBreadcrumbs } from '../../components/GlobalBreadcrumbs';
import { SideBarLayout, SplitPageLayout, SimplePageLayout } from './types';
import { generateHeights } from './utils/calcs';
import { isSideBarLayout, isSimplePageLayout, isSplitPageLayout } from './utils/detectLayoutType';
export * from './types';

type Navigation = {
  /**
   * The navigation component
   */
  navigation: React.ReactNode;
};

type Header = {
  /**
   * The header component
   */
  header?: React.ReactNode;
};

type Alert = {
  /**
   * The alert component
   */
  alert?: {
    severity: AlertColor;
    message: string;
  };
};

type CommonProps = {
  /**
   * Contents overflow setting
   */
  overflow?: 'auto' | 'none';
  /**
   * A boolean to hide the header divider
   */
  hideHeaderDivider?: boolean;
  /**
   * Chat needs to go full width on small screens
   */
  removePaddingOnSmallScreens?: boolean;
};

type FooterProps = {
  /**
   * The footer component
   */
  footer: React.ReactNode;
  /**
   * Accepts either a Boolean indicating whether the footer should be shown. Also accepts a string 'half' for
   * SplitPageLayout ONLY which indicates footer should be 50% of screen width.
   */
  showFooter: boolean | 'half';
};

type CommonWithFooterProps = CommonProps & FooterProps;
type CommonWithoutFooterProps = CommonProps & { footer?: never; showFooter?: never };

// use discriminate types to provide a final type per layout, this will help prevent forgotten headers etc.
type LayoutProps =
  | ({ layoutType: 'sidebarLayoutList' } & SideBarLayout & Navigation & Header & CommonWithoutFooterProps & Alert)
  | ({ layoutType: 'sidebarLayoutDetails' } & SideBarLayout & Navigation & Header & CommonWithoutFooterProps & Alert)
  | ({ layoutType: 'simplePageLayout' } & SimplePageLayout &
      Navigation &
      Header &
      Partial<CommonWithFooterProps> &
      Alert)
  | ({ layoutType: 'splitPageLayout' } & SplitPageLayout &
      Partial<Navigation> &
      Header &
      CommonWithFooterProps &
      Alert);

/**
 * Responsive Layout component which accepts components as params to produce a page with a navigation and a header
 * @returns Layout with responsive nav, footer (if specified) and supplied child component(s) as content
 */
export const CoreLayout: React.FC<React.PropsWithChildren<LayoutProps>> = ({
  navigation,
  header,
  footer,
  showFooter,
  overflow = 'none',
  // not using at the moment, but should replace the isLayout...Fns
  layoutType,
  hideHeaderDivider = false,
  alert,
  removePaddingOnSmallScreens = false,
  ...layout
}: LayoutProps) => {
  const { footerHeight, navigationHeight, constrainedHeight } = generateHeights({
    showNavigation: !!navigation,
    showFooter,
  });
  const theme = useTheme();
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'), { noSsr: true });
  const contentComponent = isSimplePageLayout(layout) ? 'main' : 'div';
  const shouldConstrainHeight = isSideBarLayout(layout) && layout?.constrainHeight;

  /**
   * Identifies and renders the correct content based on the layout provided
   */
  const renderContent = () => {
    if (isSideBarLayout(layout)) {
      const mobileMain = layout.mobileMain ? layout.mobileMain : 'main';

      return (
        <Box
          sx={{
            width: { xxs: '100%', sm: '100%', md: '100%' },
            display: 'flex',
            flexDirection: 'row',
            alignItems: layout?.constrainHeight ? 'flex-start' : undefined,
            height: layout?.constrainHeight ? '100%' : 'max(100%, 100vh)',
            gap: theme.custom.spacing?.['6xl'],
          }}
        >
          <Box
            component={smUp ? 'aside' : 'main'}
            sx={{
              width: { xxs: '100%', sm: '320px' },
              flexShrink: 0,
            }}
            bgcolor={!smUp && layout.flatMain ? '' : 'background.paper'}
          >
            {smUp ? layout.aside : layout[mobileMain]}
          </Box>
          {smUp && (
            <Box
              component="main"
              sx={{
                backgroundColor: layoutType === 'sidebarLayoutDetails' ? 'white' : 'transparent',
                width: { xxs: '100%', sm: 'calc(100% - 320px)' },
                pb: theme.custom.spacing?.['xl'],
                ...(layout?.constrainHeight && {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  boxSizing: 'border-box',
                }),
              }}
            >
              {layout.main}
            </Box>
          )}
        </Box>
      );
    } else if (isSplitPageLayout(layout)) {
      const flexDirection = layout.flexDirection ? layout.flexDirection : 'row';

      return (
        <Grid container size={{ sm: 12 }} minHeight="100%" flexDirection={flexDirection} sx={{ minWidth: 0 }}>
          <Grid container component="main" size={{ xxs: 12, sm: 6 }} minHeight="100%" sx={{ minWidth: 0 }}>
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              flexWrap="nowrap"
              data-testid="half-footer-box"
              sx={{ minWidth: 0 }}
            >
              <Box flexGrow={1} sx={{ minWidth: 0 }}>
                {layout.main}
              </Box>
              {/* If the footer is half then render it within this box so it inherits the parent width */}
              {showFooter === 'half' && renderFooter}
            </Box>
          </Grid>
          {smUp && (
            <Grid
              component="aside"
              size={{ sm: 6 }}
              sx={{ position: 'sticky', top: 0, height: '100vh', alignSelf: 'flex-start', minWidth: 0 }}
            >
              {layout.right}
            </Grid>
          )}
        </Grid>
      );
    } else if (isSimplePageLayout(layout)) {
      return <Box sx={{ width: '100%', height: '100%' }}>{layout.main}</Box>;
    }
    // If the layout cannot be established return null to ensure nothing is rendered
    return null;
  };

  /**
   * Identifies and renders the correct footer
   */
  const renderFooter = (
    <Grid size={{ xxs: 12 }} width="100%" height={footerHeight} data-testid="footer">
      {footer}
    </Grid>
  );

  return (
    <React.Fragment>
      <CssBaseline />
      {/* The generic layout uses a grid flexed as cols, this stacks the navigation, header, content and footer. */}
      {!!navigation && (
        <Grid
          size={{ xxs: 12 }}
          borderBottom={1}
          borderColor="divider"
          height={`${navigationHeight}px`}
          data-testid="navigation"
          position="sticky"
          top={0}
          zIndex={999}
        >
          {navigation}
        </Grid>
      )}
      {alert && (
        <Box>
          <Alert sx={{ maxWidth: '1280px', m: '0 auto', backgroundColor: '#ffffff' }} severity={alert.severity}>
            {alert.message}
          </Alert>
          <Divider />
        </Box>
      )}
      <Box
        className="main-content"
        sx={{
          height: `calc(100% - ${navigationHeight}px)`,
          overflowY: 'auto',
          position: 'relative',
          scrollbarGutter: 'stable',
        }}
      >
        <Grid
          flexDirection="column"
          flexWrap="nowrap"
          height="100%"
          container
          maxWidth={isSplitPageLayout(layout) ? 'unset' : '1280px'}
          sx={{
            margin: '0 auto',
            px: !layout.main
              ? theme?.custom?.spacing?.['xl']
              : isSplitPageLayout(layout) || (removePaddingOnSmallScreens && !smUp)
              ? undefined
              : theme?.custom?.spacing?.['3xl'],
          }}
        >
          {!!header && (
            <>
              <Grid
                size={{ xxs: 12 }}
                data-testid="header"
                mb={theme.custom.spacing?.['xl']}
                mt={theme.custom.spacing?.['6xl']}
              >
                <Grid size={{ xxs: 12 }} mb={theme.custom.spacing?.['xl']}>
                  <GlobalBreadcrumbs />
                </Grid>

                {header}
              </Grid>
              {!hideHeaderDivider && <Divider sx={{ mb: theme?.custom?.spacing?.['4xl'] }} />}
            </>
          )}
          <Grid
            container
            size={{ xxs: 12 }}
            flexDirection="row"
            flexGrow={1}
            overflow={overflow}
            component={contentComponent}
            height={shouldConstrainHeight ? constrainedHeight : undefined}
          >
            {renderContent()}
          </Grid>
          {/* Render a footer only when footer is true, half footer is handled in the SplitPageLayout */}
          {showFooter === true && renderFooter}
        </Grid>
      </Box>
    </React.Fragment>
  );
};
